import * as _ from 'lodash';
import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { OptionsType, ValueType } from 'react-select-2/lib/types';

import { window } from '../../globals';
import { useAsyncStatus } from '../../hooks';
import * as api from '../../redux/api';
import { nuc } from '../../utils';
import { AsyncSelect } from '../generic';
import { ButtonGroup, InlineLoaderButton } from '../generic/Button';
import type { FailedFetchResponse } from '../../helpers/errors';
import type { Organization } from '../../types';

type MinOrg = Pick<Organization, 'id' | 'title' | 'access_level'>;

type OrgSelectType = {
  value: MinOrg;
  label: string;
};

const isOrgOption = (opt: OrgSelectType | OptionsType<OrgSelectType> | null | undefined): opt is OrgSelectType => {
  return !_.isNil(opt) && !Array.isArray(opt);
};

const ChangeOrgCard: VoidFunctionComponent = () => {
  const [selectedOrg, setSelectedOrg] = useState<MinOrg | null>(null);
  const { status, onError, onPending, onSuccess } = useAsyncStatus();

  const loadOrganizations = useCallback((query: string, cb) => {
    api
      .query<never, MinOrg[]>('/api/organizations', { method: 'GET', query: { search: nuc(query) } })
      .then((orgs) => {
        return orgs?.data?.map((org) => ({ value: org, label: `${org.title} (${org.access_level})` })) || [];
      })
      // eslint-disable-next-line promise/no-callback-in-promise --  This is the debounced query will work with AsyncSelect
      .then(cb)
      .catch(onError);
  }, [onError]);

  const debouncedLoadOrgs = _.debounce(loadOrganizations, 500);

  const selectOrganization = useCallback((option: ValueType<OrgSelectType>) => {
    if (option && isOrgOption(option) && option.value) {
      setSelectedOrg(option.value);
    }
  }, []);

  const onChangeOrgSubmit = useCallback(async () => {
    if (!selectedOrg) {
      return;
    }

    onPending();
    try {
      await api.put(`/api/users/${window.user?.id}`, { organizationId: selectedOrg.id });
      onSuccess();
      window.location.reload();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [selectedOrg, onError, onPending, onSuccess]);

  const changeToFOSSA = useCallback(async () => {
    onPending();
    try {
      await api.put(`/api/users/${window.user?.id}`, { organizationId: 1 });
      onSuccess();
      window.location.reload();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [onError, onPending, onSuccess]);

  const onSelectEnter = useCallback((e) => {
    // If the menu is closed and the user presses enter, submit the org change
    if (e.key === 'Enter') {
      onChangeOrgSubmit();
    }
  }, [onChangeOrgSubmit]);

  return (
    <div className="card">
      <h3 className="nomargin-v">Change Organization</h3>
      {status.error && <div className="alert alert-error">{status.error}</div>}
      {status.errors && !_.isEmpty(status.errors) &&
        status.errors.map((err) => (
          <div className="alert alert-error">{err}</div>
        ))}
      <br />
      <AsyncSelect
        onChange={selectOrganization}
        loadOptions={debouncedLoadOrgs}
        value={selectedOrg && { value: selectedOrg, label: `${selectedOrg.title} (${selectedOrg.access_level})` }}
        defaultOptions
        onKeyDown={onSelectEnter}
        placeholder="Search Organizations by Title or ID..."
      />
      <br />
      <ButtonGroup>
        <InlineLoaderButton
          onClick={onChangeOrgSubmit}
          isDisabled={status.loading || !selectedOrg}
          isLoading={status.loading}
          type="slate"
        >
          {!selectedOrg ? (
            'Select an org'
          ) : (
            <span>
              Change org to <b>{selectedOrg.title}</b>
            </span>
          )}
        </InlineLoaderButton>
        {!window.on_prem && (
          <InlineLoaderButton onClick={changeToFOSSA} type="secondary" isLoading={status.loading}>
            Change to FOSSA
          </InlineLoaderButton>
        )}
      </ButtonGroup>
    </div>
  );
};

export default ChangeOrgCard;
